import React from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, CircularProgress, LinearProgress } from '@mui/material';

export default function BulkTranslateDialog({
  open,
  supportedLanguages,
  baseLang,
  onBaseLangChange,
  itemCount,
  translating,
  results,
  onClose,
  onStart,
}) {
  const { t } = useTranslation();

  return (
    <Dialog
      open={open}
      onClose={() => { if (!translating) onClose(); }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span>🌐</span>
          <span>{t('translation.bulkTranslateTitle')}</span>
        </div>
      </DialogTitle>
      <DialogContent>
        {!results ? (
          <>
            <p style={{ marginBottom: '1rem', color: 'var(--text-muted)' }}>
              {t('translation.bulkTranslateDescription', { count: itemCount })}
            </p>

            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label>{t('translation.sourceLanguage')}</label>
              <select
                className="form-input"
                value={baseLang}
                onChange={(e) => onBaseLangChange(e.target.value)}
                disabled={translating}
              >
                {supportedLanguages.map((lang) => (
                  <option key={lang.code} value={lang.code}>
                    {lang.flag} {lang.nativeName}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                {t('translation.targetLanguages')}
              </label>
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                {supportedLanguages.filter((l) => l.code !== baseLang).map((lang) => (
                  <div key={lang.code} style={{
                    display: 'flex', alignItems: 'center', gap: '0.5rem',
                    padding: '0.5rem 1rem', background: 'var(--surface-alt)', borderRadius: '8px',
                  }}>
                    <span>{lang.flag}</span>
                    <span>{lang.nativeName}</span>
                    <span style={{ color: 'var(--accent)' }}>✓</span>
                  </div>
                ))}
              </div>
            </div>

            {translating && (
              <div style={{ marginTop: '1.5rem' }}>
                <LinearProgress variant="indeterminate" />
                <p style={{ textAlign: 'center', marginTop: '0.5rem', color: 'var(--text-muted)' }}>
                  {t('translation.translatingAllItems')}
                </p>
              </div>
            )}
          </>
        ) : (
          <div>
            <div style={{
              textAlign: 'center', padding: '1rem',
              background: results.failed === 0 ? '#f0fdf4' : '#fef2f2',
              borderRadius: '8px', marginBottom: '1rem',
            }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>
                {results.failed === 0 ? '✅' : '⚠️'}
              </div>
              <h4 style={{ margin: '0 0 0.5rem 0' }}>
                {t('translation.bulkComplete')}
              </h4>
              <p style={{ margin: 0, color: 'var(--text-muted)' }}>
                {t('translation.bulkStats', {
                  translated: results.translated,
                  failed: results.failed,
                  total: results.total,
                })}
              </p>
            </div>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={translating}>
          {results ? t('common.close') : t('common.cancel')}
        </Button>
        {!results && (
          <Button
            onClick={onStart}
            variant="contained"
            color="primary"
            disabled={translating || itemCount === 0}
          >
            {translating ? (
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <CircularProgress size={18} color="inherit" />
                {t('translation.translating')}
              </span>
            ) : (
              t('translation.startTranslation')
            )}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
